import * as dateFns from 'date-fns';
import { ApexOptions } from 'apexcharts';
import { enAU } from 'date-fns/locale';
import { SensorReadingGraphResponse, GraphSeries, SensorStatus } from "./requests";
import { UrlHelper } from './url-helper';
import { formatDate, formatDateAndTime, formatShortTime, isSameDay } from './date-utils';

export class LineChart {
    public static MaxDaysInView = 7; // this is also controlled server side
    private chart: ApexCharts | undefined;
    private series: GraphSeries[] = [];

    constructor(
        private chartElementId: string,
        private mode: "dashboard" | "focused",
        public fromDate: Date,
        public toDate: Date,
        private dataFetch: (fromDate: Date, toDate: Date) => JQueryPromise<SensorReadingGraphResponse>,
        private urlHelper: UrlHelper,
        private timeZoneId: string) {
        
        if (dateFns.differenceInDays(this.toDate, this.fromDate) > LineChart.MaxDaysInView) {
            this.toDate = dateFns.addDays(this.fromDate, LineChart.MaxDaysInView);
        }
    }

    public async firstRender() {
        if (this.chart) {
            this.chart.destroy();
        }

        const element = document.getElementById(this.chartElementId);
        this.chart = new ApexCharts(
            element,
            this.getLineChartOptions(),
        );
        this.chart.render();
        await this.requestDataAndUpdate();
    }

    public async updateWindow(newFromDate: Date, newToDate: Date) {
        this.fromDate = newFromDate;
        this.toDate = newToDate;
        await this.requestDataAndUpdate();
    }

    private async requestDataAndUpdate() {
        try {
            const data = await this.dataFetch(this.fromDate, this.toDate);
            this.series = data.series;
            if (data.series && data.series.length) {
                this.chart.updateSeries(data.series, true);
                for (const annotation of data.annotations) {
                    const apexAnnotation: YAxisAnnotations = {
                        y: annotation.value,
                        borderColor: '#ff5b5b',
                        label: {
                            borderColor: '#ff5b5b',
                            style: {
                                color: '#fff',
                                background: '#ff5b5b'
                            },
                            text: this.mode === "focused" ? annotation.name : undefined,
                        }
                    };
                    this.chart.addYaxisAnnotation(apexAnnotation, true);
                }
            }

            if (data.series && data.series.length && data.series.some(s => !!s.data.length)) {
                await this.chart.updateOptions({
                    group: "line-chart-synced-group",
                    yaxis: this.getYAxises(data.series),
                });
            } else {
                const noDataText = isSameDay(this.fromDate, this.toDate, this.timeZoneId)
                    ? `No data (${formatDateAndTime(this.fromDate, this.timeZoneId)}-${formatShortTime(this.toDate, this.timeZoneId)})`
                    : `No data (${formatDateAndTime(this.fromDate, this.timeZoneId)}-${formatDateAndTime(this.toDate, this.timeZoneId)})`;
                await this.chart.updateOptions({
                    noData: { text: noDataText },
                    xaxis: {
                        min: this.fromDate.getTime(),
                        max: this.toDate.getTime(),
                    },
                    group: null,
                }, false, false, false);
            }
        } catch (e) {
            await this.chart.updateOptions({ noData: { text: "Error" } }, false, false, false)
            throw (e);
        }
    }

    public destroy() {
        if (this.chart) {
            this.chart.destroy();
            delete this.chart;
        }
    }

    public getMillisecondsInView() {
        return dateFns.differenceInMilliseconds(this.toDate, this.fromDate);
    }

    private getLineChartOptions(): ApexOptions {
        const options: ApexOptions = {
            chart: {
                id: this.chartElementId,
                type: "line",
                zoom: {
                    enabled: false,
                },
                toolbar: {
                    export: {
                        csv: {
                            columnDelimiter: ",",
                            headerCategory: "Timestamp",
                            dateFormatter(timestamp) {
                                return new Date(timestamp).toISOString();
                            },
                        },
                    },
                },
                events: {
                    legendClick: (chartContext, seriesIndex, config) => {
                        const clickedSeries = this.series[seriesIndex];
                        if (clickedSeries && clickedSeries.sensorId) {
                            this.urlHelper.navigate(
                                this.urlHelper.formatSensorDetailUrl(clickedSeries.sensorId)
                            );
                        }
                    },
                    click: (event, chartContext, config) => {
                        if (typeof config.seriesIndex === "number" && config.seriesIndex >= 0) {
                            this.urlHelper.navigate(
                                this.urlHelper.formatSensorDetailUrl(
                                    this.series[config.seriesIndex].sensorId
                                )
                            );
                        }
                    },
                },
            },
            series: this.series,
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: "straight",
            },
            grid: {
                row: {
                    colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
                    opacity: 0.5,
                },
            },
            xaxis: {
                type: "datetime",
                min: this.fromDate.getTime(),
                max: this.toDate.getTime(),
                labels: {
                    formatter: (value, timestamp, opts) => {
                        if (isSameDay(this.fromDate, this.toDate, this.timeZoneId)
                            && isSameDay(this.fromDate, new Date(), this.timeZoneId)) {
                            return formatShortTime(new Date(timestamp), this.timeZoneId);
                        }

                        const dateFormat = Intl.DateTimeFormat('en-AU', { day: '2-digit', month: '2-digit', timeZone: this.timeZoneId });
                        return `${dateFormat.format(new Date(timestamp))} ${formatShortTime(new Date(timestamp), this.timeZoneId)}`;
                    },
                    rotate: 0,
                },
                tooltip: {
                    enabled: false,
                },
            },
            yaxis: [],
            legend: {
                show: true,
                showForSingleSeries: true,
            },
            tooltip: {
                x: {
                    show: true,
                    formatter: (val: number) => {
                        return formatDateAndTime(new Date(val), this.timeZoneId);
                    },
                },
                y: {
                    formatter: (val: number, { seriesIndex }) => {
                        if (this.series) {
                            switch (this.series[seriesIndex].measurementType) {
                                case "Humidity":
                                case "BatteryLevel":
                                    if (typeof val === "number") {
                                        return `${val.toFixed(2)}%`;
                                    }
                                    return "";
                                case "Temperature":
                                    if (typeof val === "number") {
                                        return `${val.toFixed(2)}℃`;
                                    }
                                    return "";
                                case "Pressure":
                                case "PressureDiff":
                                    if (typeof val === "number") {
                                        return `${val.toFixed(2)} hPa`;
                                    }
                                    return "";
                                case "OpenClose":
                                    if (typeof val === "number") {
                                        return val === 1 ? "Open" : "Closed";
                                    }
                                    return "";
                                default:
                                    return "";
                            }
                        }
                        return "";
                    },
                },
            },
            noData: {
                text: "Loading ⌛",
            },
        };

        return options;
    }

    private getYAxises(dataSeries: GraphSeries[]): ApexYAxis[] {
        let yAxises: ApexYAxis[] = [];
        if (dataSeries.length === 0) {
            yAxises.push(getYAxis("Temperature", false, true, "℃"));
        } else {
            let masterHumidityName: string | undefined;
            let masterHumidityNameIndex: number | undefined;
            let masterTemperatureName: string | undefined;
            let masterTemperatureNameIndex: number | undefined;
            let masterPressureName: string | undefined;
            let masterPressureNameIndex: number | undefined;
            let masterOpenCloseName: string | undefined;
            let masterOpenCloseIndex: number | undefined;
            for (let seriesCounter = 0; seriesCounter < dataSeries.length; seriesCounter++) {
                switch (dataSeries[seriesCounter].measurementType) {
                    case "Humidity":
                    case "BatteryLevel":
                        if (!masterHumidityName) {
                            masterHumidityName = dataSeries[seriesCounter].name;
                            masterHumidityNameIndex = seriesCounter;
                        }
                        if (dataSeries.length === 1) {
                            yAxises.push(getYAxis(masterHumidityName, false, dataSeries[seriesCounter].name === masterHumidityName
                                && seriesCounter === masterHumidityNameIndex, "%"));
                        } else {
                            yAxises.push(getYAxis(masterHumidityName, true, dataSeries[seriesCounter].name === masterHumidityName
                                && seriesCounter === masterHumidityNameIndex, "%"));
                        }
                        break;
                    case "Temperature":
                        if (!masterTemperatureName) {
                            masterTemperatureName = dataSeries[seriesCounter].name;
                            masterTemperatureNameIndex = seriesCounter;
                        }
                        yAxises.push(getYAxis(masterTemperatureName, false, dataSeries[seriesCounter].name === masterTemperatureName
                            && seriesCounter === masterTemperatureNameIndex, "℃"));
                        break;
                    case "Pressure":
                    case "PressureDiff":
                        // todo needs testing with real sensor data - this is a guess
                        if (!masterPressureName) {
                            masterPressureName = dataSeries[seriesCounter].name;
                            masterPressureNameIndex = seriesCounter;
                        }
                        yAxises.push(getYAxis(masterPressureName, false, dataSeries[seriesCounter].name === masterPressureName
                            && seriesCounter === masterPressureNameIndex, "hPa"));
                        break;
                    case "OpenClose":
                        // todo needs testing with real sensor data - this is a guess
                        if (!masterOpenCloseName) {
                            masterOpenCloseName = dataSeries[seriesCounter].name;
                            masterOpenCloseIndex = seriesCounter;
                        }
                        yAxises.push(getYAxis(masterOpenCloseName, false, dataSeries[seriesCounter].name === masterOpenCloseName
                            && seriesCounter === masterOpenCloseIndex, ""));
                        break;
                    default:
                        throw new Error("Invalid measurement type.");
                }
            }
        }

        return yAxises;

        function getYAxis(seriesName: string, isOppositeYAxis: boolean, shouldShow: boolean, measurementSymbol: string): ApexYAxis {
            return {
                opposite: isOppositeYAxis,
                seriesName: seriesName,
                show: shouldShow,
                labels: {
                    minWidth: 5,
                    formatter: (val: any) => {
                        if (typeof val === "number") {
                            return `${val.toFixed(2)}` + `${measurementSymbol}`;
                        }
                        return "";
                    }
                }
            }
        }
    }
}

export class SensorStatusPieChart {
    private chart: ApexCharts | null = null;

    constructor(private totalSensors: number, private offlineSensors: number, private alertingSensors: number, private elementId: string, private locationId: number | undefined, private urlHelper: UrlHelper) {
    }

    public render() {
        const options: ApexOptions = {
            chart: {
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        const sensorStatusIndex = parseInt(config.dataPointIndex, 10);
                        // add to sensorStatusIndex because unlike config.dataPointIndex, in the HTML dropdowns, All = 0, Online = 1, Offline = 2, Alerting = 3 
                        const sensorStatusString = SensorStatus[sensorStatusIndex + 1];
                        const sensorStatus = SensorStatus[sensorStatusString as keyof typeof SensorStatus];
                        if (typeof this.locationId !== "undefined") {
                            this.urlHelper.navigate(this.urlHelper.formatSensorUrl(sensorStatus, this.locationId));
                        } else {
                            this.urlHelper.navigate(this.urlHelper.formatAllLocationsSensorUrl(sensorStatus));
                        }
                    }
                },
                type: "donut",
                height: 150
            },
            series: [this.totalSensors - this.offlineSensors - this.alertingSensors, this.offlineSensors, this.alertingSensors],
            labels: ["Online", "Offline", "Alerting"],
            colors: ["#00E396", "#F9BC0D", "#FF4560"],
            legend: {
                show: true,
            },
            dataLabels: {
                enabled: false
            },
        };
        this.chart = new ApexCharts(
            document.getElementById(this.elementId),
            options);
        this.chart.render();
    }

    public destroy() {
        if (this.chart) {
            this.chart.destroy();
            delete this.chart;
        }
    }
}

export class AlertHistoryLineChart {
    private chart: ApexCharts | null = null;

    constructor(private data: Array<{ x: string, y: number }>, private elementId: string) {

    }

    public render() {
        const options: ApexOptions = {
            chart: {
                type: "line",
                height: 100,
                toolbar: {
                    show: false,
                },
            },
            // select red colour from default theme https://apexcharts.com/docs/options/theme/ 
            colors: ["#FF4560"],
            stroke: {
                curve: "smooth"
            },
            series: [{
                name: "Active Alerts",
                data: this.data
            }],
            legend: {
                show: false,
            },
            dataLabels: {
                enabled: false
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
                axisBorder: {
                    show: false,
                },
                tooltip: {
                    enabled: false,
                }
            },
            yaxis: {
                labels: {
                    show: false,
                },
            },
            grid: {
                show: false,
            },
            tooltip: {
                y: {
                    formatter: (val: number) => {
                        return val.toFixed(0);
                    }
                }
            },
        };
        this.chart = new ApexCharts(
            document.getElementById(this.elementId),
            options);
        this.chart.render();
    }

    public destroy() {
        if (this.chart) {
            this.chart.destroy();
            delete this.chart;
        }
    }
}
